import { datadog } from 'utils'

import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE, S3_SUCCESS_STATUS_CODES } from './consts'
import type { BatchUploadToS3Params, BatchUploadToS3Result, UploadToS3Params, UploadToS3Result } from './types'

class DocumentsService {
  /**
   * Validates file before upload
   * @param file - The file to validate
   * @returns true if valid, throws error otherwise
   */
  private validateFile(file: File): boolean {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
    }

    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed`)
    }

    // Check file extension
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      throw new Error(`File extension ${fileExtension} is not allowed`)
    }

    return true
  }

  /**
   * Uploads a file to S3 using a presigned URL
   * @param params - Upload parameters including file, upload config, and optional progress callback
   * @returns Promise with upload result
   */
  public uploadToS3(params: UploadToS3Params): Promise<UploadToS3Result> {
    const { file, uploadConfig, onProgress } = params

    return new Promise((resolve, reject) => {
      try {
        this.validateFile(file)

        const formData = new FormData()

        uploadConfig.fields.forEach(field => {
          formData.append(field.key, field.value)
        })

        formData.append('file', file)

        const xhr = new XMLHttpRequest()

        if (onProgress) {
          xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100
              onProgress(percentComplete)
            }
          }
        }

        xhr.onload = () => {
          if (xhr.status >= S3_SUCCESS_STATUS_CODES.MIN && xhr.status <= S3_SUCCESS_STATUS_CODES.MAX) {
            resolve({
              success: true,
              fileId: uploadConfig.id,
            })
          } else {
            const error = new Error(`Upload failed with status: ${xhr.status}`)

            datadog.dispatchGenericErrorLog({
              log: {
                from: 'src/services/documents',
                name: 'S3 Upload Failed',
                message: `Failed to upload file to S3. Status: ${xhr.status}`,
              },
              error,
            })

            reject({
              success: false,
              fileId: uploadConfig.id,
              error,
            })
          }
        }

        xhr.onerror = () => {
          const error = new Error('Network error during upload')

          datadog.dispatchGenericErrorLog({
            log: {
              from: 'src/services/documents',
              name: 'S3 Upload Network Error',
              message: 'Network error occurred during file upload to S3',
            },
            error,
          })

          reject({
            success: false,
            fileId: uploadConfig.id,
            error,
          })
        }

        xhr.onabort = () => {
          const error = new Error('Upload was aborted')

          reject({
            success: false,
            fileId: uploadConfig.id,
            error,
          })
        }

        xhr.open('POST', uploadConfig.uploadUrl)
        xhr.send(formData)
      } catch (error) {
        const uploadError = error instanceof Error ? error : new Error('Unknown error during file upload')

        datadog.dispatchGenericErrorLog({
          log: {
            from: 'src/services/documents',
            name: 'S3 Upload Validation Error',
            message: uploadError.message,
          },
          error: uploadError,
        })

        reject({
          success: false,
          fileId: uploadConfig.id,
          error: uploadError,
        })
      }
    })
  }

  /**
   * Uploads multiple files to S3 using presigned URLs in parallel
   * @param params - Batch upload parameters including files array, progress callback, and completion callback
   * @returns Promise with batch upload result
   */
  public async batchUploadToS3(params: BatchUploadToS3Params): Promise<BatchUploadToS3Result> {
    const { files, onProgress, onFileComplete } = params

    const uploadPromises = files.map(fileItem =>
      this.uploadToS3({
        file: fileItem.file,
        uploadConfig: fileItem.uploadConfig,
        onProgress: onProgress ? progress => onProgress(fileItem.uploadConfig.id, progress) : undefined,
      })
        .then(result => {
          if (onFileComplete) {
            onFileComplete(result)
          }
          return result
        })
        .catch(error => {
          const uploadResult = error as UploadToS3Result
          if (onFileComplete) {
            onFileComplete(uploadResult)
          }
          return uploadResult
        })
    )

    const settledResults = await Promise.allSettled(uploadPromises)

    const results: UploadToS3Result[] = settledResults.map(settled => {
      if (settled.status === 'fulfilled') {
        return settled.value
      } else {
        // This should rarely happen since we catch errors in the promise chain
        return {
          success: false,
          fileId: 'unknown',
          error: settled.reason instanceof Error ? settled.reason : new Error('Unknown error'),
        }
      }
    })

    const successCount = results.filter(r => r.success).length
    const failureCount = results.filter(r => !r.success).length

    return {
      results,
      successCount,
      failureCount,
    }
  }
}

export const documentsService = new DocumentsService()
